import { TIPO_EMPRESA_USUARIO } from "api/Types/TipoEmpresa"
import DireccionInputs from "common/components/FormikFields/DireccionInputs"
import FormikAutoComplete from "common/components/FormikFields/FormikAutoComplete"
import FormikInputText from "common/components/FormikFields/FormikInputText"
import useRazonSocialAndCif from "common/hooks/useRazonSocialAndCif"
import useSearchEmpresa from "common/hooks/useSearchEmpresa"
import {
  validateCodigoPostal,
  validatePoblacion,
} from "common/utils/formValidations"
import { validateSpanishID } from "common/utils/spanishIdValidator"
import { withFormik } from "formik"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"
import { DEFAULT_COUNTRY_SELECTED } from "../../../../common/constants/countries"
import "./CrearEmpresaForm.scss"
import { adapterNuevaEmpresa } from "./constants"

const CrearEmpresaForm = ({
  onHide,
  handleSubmit,
  setFieldValue,
  values,
  errors,
  touched,
  setFieldTouched,
  isSubmitting,
  isEditing = false,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => {
  const { t } = useTranslation()
  const {
    razonesSociales,
    cifs,
    getByCifOrRazonSocial,
    getRazonSocialByCif,
    getCifByRazonSocial,
  } = useRazonSocialAndCif()

  const { searchingEmpresa, searchEmpresaByNif } = useSearchEmpresa({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSearchEmpresa: (empresa: any) => {
      setFieldValue("direccion", empresa.direccion?.calle)
      setFieldValue("cp", empresa.direccion?.codigoPostal)
      setFieldValue("poblacion", empresa.direccion?.poblacionNombre)
      setFieldValue("poblacionId", empresa.direccion?.poblacionId)
      setFieldValue("provinciaId", empresa.direccion?.provinciaId)
      setFieldValue("provincia", empresa.direccion?.provincia)
    },
  })

  const handleOnSelectRazonSocial = (e: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    originalEvent: any
    value: string
  }) => {
    setFieldValue("razonSocial", e.value)
    const cif = getCifByRazonSocial(e.value)
    setFieldValue("cif", cif)

    if (!isEditing) {
      searchEmpresaByNif(cif)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnSelectCif = (e: any) => {
    setTimeout(() => setFieldValue("cif", e.value), 50)
    setFieldValue("cif", e.value)
    const razonSocial = getRazonSocialByCif(e.value)
    setFieldValue("razonSocial", razonSocial)

    if (!isEditing) {
      searchEmpresaByNif(e.value)
    }
  }

  return (
    <div className='tw-px-10 tw-pb-10'>
      <form className='tw-w-full tw-rounded-none'>
        <div className='tw-flex tw-flex-col tw-w-full tw-mb-4'>
          <label htmlFor='cif'>
            {TIPO_EMPRESA_USUARIO.AUTONOMO === values.mode
              ? "DNI"
              : t("seleccionDeEmpresa.modal.labelNif")}
          </label>
          <div className='selected-country-mix-cif !tw-w-full !tw-h-fit'>
            {values.mode !== TIPO_EMPRESA_USUARIO.AUTONOMO ? (
              <div className='tw-w-full tw-flex tw-flex-col'>
                <FormikAutoComplete
                  autoFocus
                  id='cif'
                  className='!tw-w-full [&>*:first-child]:!tw-w-full !tw-h-10 !tw-rounded'
                  name='cif'
                  disabled={isEditing || searchingEmpresa}
                  completeMethod={(query: string) => {
                    getByCifOrRazonSocial("cif", query)
                  }}
                  suggestions={cifs}
                  showError={false}
                  onSelect={handleOnSelectCif}
                />
                {errors.cif && touched.cif && (
                  <small className='p-error p-d-block'>{errors.cif}</small>
                )}
              </div>
            ) : (
              <div className='tw-w-full tw-flex tw-flex-col'>
                <InputText
                  className={`!tw-w-full !tw-h-10 ${
                    errors.cif && touched.cif ? "!tw-border-danger" : ""
                  } !tw-rounded`}
                  autoFocus
                  id='cif'
                  onChange={(ev) => setFieldValue("cif", ev.target.value)}
                  onBlur={() => setFieldTouched("cif", true)}
                />
                {errors.cif && touched.cif && (
                  <small className='p-error p-d-block'>{errors.cif}</small>
                )}
              </div>
            )}
          </div>
        </div>
        <div className='tw-flex tw-flex-col tw-w-full tw-mb-6'>
          {values.mode !== TIPO_EMPRESA_USUARIO.AUTONOMO ? (
            <div className='tw-col-span-8'>
              <label
                htmlFor='razonSocial'
                className='tw-block'
              >
                {t("seleccionDeEmpresa.modal.labelCompanyName")}
              </label>

              {values?.codigoPais === DEFAULT_COUNTRY_SELECTED ? (
                <div className='tw-w-full tw-flex tw-flex-col'>
                  <FormikAutoComplete
                    id='razonSocial'
                    name='razonSocial'
                    disabled={isEditing || searchingEmpresa}
                    completeMethod={(query: string) => {
                      getByCifOrRazonSocial("razonSocial", query)
                    }}
                    suggestions={razonesSociales}
                    onSelect={handleOnSelectRazonSocial}
                    className='tw-w-full [&>*:first-child]:!tw-w-full !tw-rounded'
                    showError={false}
                  />
                  {errors.razonSocial && touched?.razonSocial && (
                    <small className='p-error p-d-block'>
                      {errors.razonSocial}
                    </small>
                  )}
                </div>
              ) : (
                <FormikInputText
                  disabled={isEditing || searchingEmpresa}
                  id='razonSocial'
                  name='razonSocial'
                  className='tw-w-full !tw-rounded'
                  onBlur={() => setFieldTouched("razonSocial", true)}
                />
              )}
            </div>
          ) : (
            <div className='tw-w-full tw-grid tw-grid-cols-2 tw-gap-6'>
              <div className='tw-col-span-1 tw-flex tw-flex-col'>
                <span>{t("seleccionDeEmpresa.modal.labelName")}</span>
                <InputText
                  value={values.nombre}
                  id='nombre'
                  className={`tw-w-full ${
                    errors?.nombre && touched.nombre ? "!tw-border-danger" : ""
                  }`}
                  onChange={(evento) =>
                    setFieldValue("nombre", evento.target.value)
                  }
                  onBlur={() => setFieldTouched("nombre", true)}
                />
                {errors.nombre && touched.nombre && (
                  <small className='p-error p-d-block'>{errors.nombre}</small>
                )}
              </div>
              <div className='tw-col-span-1 tw-flex tw-flex-col'>
                <span>{t("seleccionDeEmpresa.modal.labelSubName")}</span>

                <InputText
                  value={values.apellidos}
                  id='apellidos'
                  className={`tw-w-full ${
                    errors?.apellidos && touched.apellidos
                      ? "!tw-border-danger"
                      : ""
                  } !tw-rounded`}
                  onChange={(evento) =>
                    setFieldValue("apellidos", evento.target.value)
                  }
                  onBlur={() => setFieldTouched("apellidos", true)}
                />
                {errors.apellidos && touched.apellidos && (
                  <small className='p-error p-d-block'>
                    {errors.apellidos}
                  </small>
                )}
              </div>
            </div>
          )}
        </div>

        <p className='tw-text-xl tw-font-medium tw-mb-4'>
          {t("seleccionDeEmpresa.modal.labelDirection")}
        </p>
        <div className='tw-mb-4 tw-w-full'>
          <label
            htmlFor='direccion'
            className='tw-block'
          >
            {t("seleccionDeEmpresa.modal.labelDirection")}
          </label>
          <InputText
            value={values.direccion}
            id='direccion'
            className={`tw-w-full ${
              errors?.direccion && touched.direccion ? "!tw-border-danger" : ""
            } !tw-rounded`}
            onChange={(evento) =>
              setFieldValue("direccion", evento.target.value)
            }
            onBlur={() => setFieldTouched("direccion", true)} // Esto asegura que el campo se marca como "touched" en blur
          />
          {errors.direccion && touched.direccion && (
            <small className='p-error p-d-block'>{errors.direccion}</small>
          )}
        </div>

        <div className='tw-mt-4 tw-flex tw-flex-col lg:tw-flex-row lg:tw-space-x-2 tw-mb-6'>
          <DireccionInputs
            setFieldValue={setFieldValue}
            initialCodigoPostal={values.cp}
            otherCountries={Boolean(
              values.codigoPais !== DEFAULT_COUNTRY_SELECTED
            )}
            countrySelected={values.codigoPais}
            cpValue={values.cp}
            provinciaValue={values.provincia}
            poblacionValue={values.poblacion}
            className='tw-text-left tw-w-full'
          />
        </div>
      </form>
      <div className='tw-grid tw-grid-cols-2 tw-gap-6'>
        <Button
          label={`${t("seleccionDeEmpresa.modal.cancelButton")}`}
          className='!tw-bg-transparent !tw-border-none !tw-text-text !tw-h-12 !tw-rounded'
          onClick={() => onHide()}
        />

        <Button
          label={`${t("seleccionDeEmpresa.modal.createButton")}`}
          className='!tw-bg-primary !tw-border-primary hover:!tw-bg-primary-over hover:!tw-border-primary-over hover:!tw-shadow !tw-h-12 !tw-rounded'
          loading={isSubmitting}
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}

const RevisionMultipleEstadosForm = withFormik({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapPropsToValues: ({ mode }: any) => ({
    mode,
    cif: undefined,
    razonSocial: undefined,
    direccion: undefined,
    cp: undefined,
    poblacion: undefined,
    poblacionId: undefined,
    provincia: undefined,
    provinciaId: undefined,
    nombre: null,
    apellidos: null,
    logo: undefined,
    codigoPais: "ES",
    poblacionExtranjera: "",
  }),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: ({ t }: any) =>
    Yup.object().shape({
      cif: Yup.string().when("mode", {
        is: (mode: number | null) => mode !== null,
        then: (schema) =>
          // eslint-disable-next-line func-names
          schema.test("valid-cif", function () {
            const { mode, cif } = this.parent // Accede al mode actual

            const msgCifEmpty =
              mode !== TIPO_EMPRESA_USUARIO.AUTONOMO
                ? t("errors.nifEmpty")
                : t("errors.dni")
            const msgCifInvalid =
              mode !== TIPO_EMPRESA_USUARIO.AUTONOMO
                ? t("errors.nifInvalid")
                : t("errors.dni")

            if (!cif) {
              return this.createError({ message: msgCifEmpty })
            }

            const isValid = validateSpanishID(cif)
            if (!isValid) {
              return this.createError({ message: msgCifInvalid })
            }

            return true
          }),
        otherwise: Yup.string().nullable(),
      }),
      razonSocial: Yup.string().when("mode", {
        is: (mode: number | null) => mode !== TIPO_EMPRESA_USUARIO.AUTONOMO, // Cambia 'autonomo' por el valor que determina si se debe validar `razonSocial`
        then: Yup.string()
          .nullable()
          .required(`${t("errors.razonSocial")}`),
        otherwise: Yup.string().nullable(),
      }),

      nombre: Yup.string().when("mode", {
        is: (mode: number | null) => mode === TIPO_EMPRESA_USUARIO.AUTONOMO, // Cambia 'empleado' por el valor que determina si se debe validar `nombre`
        then: Yup.string()
          .nullable()
          .required(`${t("errors.nombre")}`),
        otherwise: Yup.string().nullable(),
      }),

      apellidos: Yup.string().when("mode", {
        is: (mode: number | null) => mode === TIPO_EMPRESA_USUARIO.AUTONOMO, // Cambia 'empleado' por el valor que determina si se debe validar `apellidos`
        then: Yup.string().nullable().required(t("errors.apellido")),
        otherwise: Yup.string().nullable(),
      }),

      direccion: Yup.string().required(t("errors.direccion")),

      cp: Yup.string().when("codigoPais", {
        is: DEFAULT_COUNTRY_SELECTED,
        then: validateCodigoPostal(t),
        otherwise: Yup.string().required(t("errors.fieldEmpty")),
      }),
      poblacionExtranjera: Yup.string().when("codigoPais", {
        is: (codigoPais: string) => codigoPais !== DEFAULT_COUNTRY_SELECTED,
        then: Yup.string().required(t("errors.fieldEmpty")),
      }),
      poblacion: Yup.string().when("codigoPais", {
        is: DEFAULT_COUNTRY_SELECTED,
        then: validatePoblacion(t),
      }),
    }),

  handleSubmit: (values, { props }) => {
    props.crearNuevaEmpresa(adapterNuevaEmpresa(values))
  },
})(CrearEmpresaForm)

export default RevisionMultipleEstadosForm
